import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { log } from '@/lib/logger';

/**
 * Idle-logout: derruba a sessão (revoke global) após N minutos sem
 * atividade. Reset em mousemove/keydown/click/visibilitychange.
 *
 * Princípio de menor privilégio: tabs esquecidas em estação compartilhada
 * deixam de ser uma janela de ataque. Toast de aviso antes de expirar.
 */
export function useIdleLogout(opts: { minutes?: number; warnSeconds?: number } = {}) {
  const minutes = opts.minutes ?? 45;
  const warnSeconds = opts.warnSeconds ?? 60;
  const lastActivity = useRef<number>(Date.now());
  const warned = useRef(false);

  useEffect(() => {
    const total = minutes * 60_000;
    const warnAt = total - warnSeconds * 1000;

    const bump = () => {
      lastActivity.current = Date.now();
      warned.current = false;
    };
    const events: (keyof DocumentEventMap)[] = ['mousemove', 'keydown', 'click', 'touchstart', 'visibilitychange'];
    events.forEach((e) => document.addEventListener(e, bump, { passive: true }));

    const interval = setInterval(async () => {
      const idle = Date.now() - lastActivity.current;
      if (idle >= total) {
        const { data } = await supabase.auth.getSession();
        if (!data.session) return;
        log.info({ idleMs: idle }, 'auth.idle.logout');
        try {
          await supabase.auth.signOut({ scope: 'global' });
        } catch (err) {
          log.warn({ err }, 'auth.idle.signout_failed');
        }
        toast.warning('Sessão encerrada por inatividade. Faça login novamente.', { duration: 8000 });
      } else if (idle >= warnAt && !warned.current) {
        warned.current = true;
        toast('Sua sessão vai expirar em breve por inatividade.', { duration: warnSeconds * 1000 });
      }
    }, 15_000);

    return () => {
      clearInterval(interval);
      events.forEach((e) => document.removeEventListener(e, bump));
    };
  }, [minutes, warnSeconds]);
}
